import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdEmail, MdMenu, MdNotifications, MdSearch, MdSettings } from 'react-icons/md';
import { subscribeToNotifications } from '../../services/notificationService';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';

const Header = ({ onMobileMenuClick }) => {
  const { userProfile } = useAuth();
  const isTeacher = userProfile?.role === 'teacher';

  const [notificationCount, setNotificationCount] = useState(0);
  const [openSupportCount, setOpenSupportCount] = useState(0);
  const [teacherChatCount, setTeacherChatCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    let unsubscribeNotifications = () => {};
    let unsubscribeSupport = () => {};
    let unsubscribeTeacherChats = () => {};

    if (!userProfile) return;

    if (userProfile.role === 'admin' || userProfile.role === 'editor') {
      // Notifications listener
      unsubscribeNotifications = subscribeToNotifications((notifications) => {
        const lastSeenStr = localStorage.getItem('lastSeenAdminNotifications');
        const lastSeen = lastSeenStr ? parseInt(lastSeenStr) : 0;
        let unreadCount = 0;
        
        notifications.forEach(n => {
          const msgTime = n.createdAt?.toMillis ? n.createdAt.toMillis() : (new Date(n.createdAt)).getTime() || 0;
          if (msgTime > lastSeen) {
            unreadCount++;
          }
        });
        
        setNotificationCount(unreadCount);
      });

      // Real-time listener: count of open support tickets
      const q = query(
        collection(db, 'support_messages'),
        where('status', '==', 'open')
      );
      unsubscribeSupport = onSnapshot(q, (snap) => {
        const lastSeenStr = localStorage.getItem('lastSeenAdminSupport');
        const lastSeen = lastSeenStr ? parseInt(lastSeenStr) : 0;
        let unreadCount = 0;

        snap.forEach(doc => {
          const data = doc.data();
          const msgTime = data.createdAt?.toMillis ? data.createdAt.toMillis() : 0;
          if (msgTime > lastSeen) {
            unreadCount++;
          }
        });
        setOpenSupportCount(unreadCount);
      });
    } else if (userProfile.role === 'teacher') {
      // Teacher chat listener
      const chatsRef = collection(db, 'chats');
      const q = query(chatsRef, where('unreadByTeacher', '==', true)); 
      unsubscribeTeacherChats = onSnapshot(q, (snap) => {
        setTeacherChatCount(snap.size);
      });
    }

    return () => {
      unsubscribeNotifications();
      unsubscribeSupport();
      unsubscribeTeacherChats();
    };
  }, [userProfile]);

  return (
    <header className="bg-white px-4 md:px-6 py-4 flex items-center justify-between gap-3 md:gap-6 border-b border-gray-200 sticky top-0 z-40">
      {/* Mobile Menu Button */}
      <button
        onClick={onMobileMenuClick}
        className="lg:hidden w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center hover:bg-gray-200 transition-colors"
      >
        <MdMenu className="text-xl text-gray-600" />
      </button>

      {/* Search Bar */}
      <div className="flex-1 max-w-2xl relative">
        <MdSearch className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg md:text-xl pointer-events-none" />
        <input
          type="text"
          placeholder="Search..."
          className="w-full pl-10 md:pl-12 pr-3 md:pr-4 py-2 md:py-3 border border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary focus:bg-white transition-all text-sm md:text-base"
        />
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-2 md:gap-3">

        {/* Messages / Support Inbox Icon */}
        <button
          onClick={() => {
            if (isTeacher) {
              localStorage.setItem('lastSeenTeacherMessages', Date.now().toString());
              setTeacherChatCount(0);
              navigate('/teacher/messages');
            } else {
              localStorage.setItem('lastSeenAdminSupport', Date.now().toString());
              setOpenSupportCount(0);
              navigate('/support');
            }
          }}
          title={isTeacher ? 'Student Messages' : 'Support Inbox'}
          className="relative w-9 h-9 md:w-11 md:h-11 bg-gray-100 rounded-xl flex items-center justify-center hover:bg-gray-200 transition-colors group"
        >
          <MdEmail className="text-lg md:text-xl text-gray-600 group-hover:text-primary transition-colors" />
          {!isTeacher && openSupportCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] md:min-w-[20px] md:h-5 px-1 bg-red-500 text-white text-[10px] md:text-xs font-bold rounded-full flex items-center justify-center animate-pulse shadow-lg shadow-red-500/40">
              {openSupportCount > 99 ? '99+' : openSupportCount}
            </span>
          )}
          {isTeacher && teacherChatCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] md:min-w-[20px] md:h-5 px-1 bg-red-500 text-white text-[10px] md:text-xs font-bold rounded-full flex items-center justify-center shadow-lg shadow-red-500/40">
              {teacherChatCount > 99 ? '99+' : teacherChatCount}
            </span>
          )}
        </button>

        {!isTeacher && (
          <>
            {/* Notifications */}
            <button
              onClick={() => {
                localStorage.setItem('lastSeenAdminNotifications', Date.now().toString());
                setNotificationCount(0);
                navigate('/notifications');
              }}
              className="relative w-9 h-9 md:w-11 md:h-11 bg-gray-100 rounded-xl flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <MdNotifications className="text-lg md:text-xl text-gray-600" />
              {notificationCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] md:min-w-[20px] md:h-5 px-1 bg-red-500 text-white text-[10px] md:text-xs font-bold rounded-full flex items-center justify-center animate-pulse shadow-lg shadow-red-500/40">
                  {notificationCount > 99 ? '99+' : notificationCount}
                </span>
              )}
            </button>

            {/* Settings */}
            <button
              onClick={() => navigate('/settings')}
              className="w-9 h-9 md:w-11 md:h-11 bg-gray-100 rounded-xl flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <MdSettings className="text-lg md:text-xl text-gray-600" />
            </button>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;
